import type { GameReminderPayload } from 'pure-js/types/InAppNotification'

/** Show game reminder in list and badge from this many ms before scheduled start. */
export const GAME_REMINDER_SHOW_BEFORE_START_MS = 3 * 60 * 1000
/** Keep game reminder visible this many ms after scheduled start (e.g. "Starting now"). */
export const GAME_REMINDER_SHOW_AFTER_START_MS = 1 * 60 * 1000
/** After this many ms past scheduled start, the reminder is disabled (history only, no navigation). */
export const GAME_REMINDER_DISABLED_AFTER_START_MS = 3 * 60 * 1000

/**
 * True when the current time is within the "show window" for a game reminder:
 * from (scheduledStartTime - 3 min) until (scheduledStartTime + 1 min).
 * Used so the bell badge and toast only consider reminders in this window;
 * list shows all reminders as history (see isGameReminderDisabled for disabled state).
 */
export function isGameReminderInShowWindow(payload: GameReminderPayload, nowMs: number): boolean {
  const startMs = new Date(payload.scheduledStartTime).getTime()
  const windowStart = startMs - GAME_REMINDER_SHOW_BEFORE_START_MS
  const windowEnd = startMs + GAME_REMINDER_SHOW_AFTER_START_MS
  return nowMs >= windowStart && nowMs <= windowEnd
}

/**
 * True when the game has already started (so the reminder can be shown as history).
 */
export function isGameReminderPastStart(payload: GameReminderPayload, nowMs: number): boolean {
  const startMs = new Date(payload.scheduledStartTime).getTime()
  return nowMs > startMs + GAME_REMINDER_SHOW_AFTER_START_MS
}

/**
 * True when the game reminder is past its actionable window (3 minutes after start).
 * When true, the item stays in the list as history but should be disabled (no navigation).
 */
export function isGameReminderDisabled(payload: GameReminderPayload, nowMs: number): boolean {
  const startMs = new Date(payload.scheduledStartTime).getTime()
  return nowMs > startMs + GAME_REMINDER_DISABLED_AFTER_START_MS
}
